import { buildSaldoInicialEntry } from "./saldoInicial";

/**
 * Construye el ledger contable de un cliente (movimientos + saldo acumulado).
 *
 * Convención de signo del saldo acumulado:
 *   - saldo > 0  → el cliente nos debe (saldo por cobrar)
 *   - saldo < 0  → le debemos al cliente (anticipos o saldo a favor del cliente)
 *
 * Débitos (suben el saldo por cobrar):
 *   - Facturas SII emitidas al cliente. NC (tipo_dte=61) entra con débito NEGATIVO.
 *
 * Créditos (bajan el saldo por cobrar):
 *   - Cobros recibidos del cliente: movimientos_caja tipo='cobro' entidad_tipo='cliente'.
 *
 * Saldo inicial opcional al 2026-01-01 entra como entry sintética (origen "INI").
 *
 * Filtrado por entidad: SIEMPRE por cliente_id numérico, nunca por nombre.
 *
 * @param {object} args
 * @param {number} args.cliId                     ID numérico del cliente.
 * @param {Array}  args.siiEmitidas               Facturas SII emitidas del país.
 * @param {Array}  args.movimientosCaja           Movimientos de caja del país desde FECHA_CORTE.
 * @param {object} [args.saldoInicial]            Saldo inicial de la entidad (o null).
 * @param {string} args.monedaPrincipal           Moneda local del país ("CLP" o "PEN").
 * @param {Function} args.toDisplayCurrency       (monto, monedaOrigen, fecha) → monto en moneda display.
 * @returns {Array} Entries ordenadas DESC por fecha, con debitoDisplay/creditoDisplay/saldo.
 */
export const construirLedgerCliente = ({
  cliId,
  siiEmitidas,
  movimientosCaja,
  saldoInicial,
  monedaPrincipal,
  toDisplayCurrency,
}) => {
  if (!cliId) return [];

  const debitos = siiEmitidas
    .filter((f) => f.cliente_id === cliId)
    .map((f) => ({
      fecha: f.fecha_emision,
      concepto: f.tipo_dte === 61 ? "NC emitida" : "Factura",
      origen: "SII",
      referencia: f.folio ? String(f.folio) : "-",
      debito: f.tipo_dte === 61 ? -Number(f.monto_total) : Number(f.monto_total),
      credito: 0,
      moneda: monedaPrincipal,
    }));

  const creditos = movimientosCaja
    .filter((m) => m.tipo === "cobro" && m.entidad_tipo === "cliente" && m.entidad_id === cliId)
    .map((m) => ({
      fecha: m.fecha,
      concepto: "Cobro",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: 0,
      credito: Number(m.monto),
      moneda: m.moneda || monedaPrincipal,
    }));

  const inicial = saldoInicial ? [buildSaldoInicialEntry(saldoInicial, monedaPrincipal)] : [];
  const entries = [...inicial, ...debitos, ...creditos].sort((a, b) => a.fecha.localeCompare(b.fecha));

  let saldo = 0;
  return entries
    .map((entry) => {
      const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
      const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
      saldo += debitoDisplay - creditoDisplay;
      return { ...entry, debitoDisplay, creditoDisplay, saldo };
    })
    .reverse();
};
